import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Building2, MapPin, BadgeCheck, ShieldCheck, Star, Search, Package, Sparkles, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const waHref = (phone, text) => {
  const digits = (phone || "").replace(/[^0-9]/g, "");
  return `https://wa.me/${digits}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
};

export const TrustStars = ({ rating = 0, size = "w-4 h-4" }) => (
  <span className="inline-flex items-center gap-0.5" data-testid="trust-stars" aria-label={`Trust ${rating}/5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`${size} ${n <= rating ? "text-amber-500 fill-amber-500" : "text-slate-300"}`} />
    ))}
  </span>
);

export const FreshlyVerifiedBadge = ({ month }) => {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 bg-sky-600 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5" data-testid="badge-fresh">
      <Sparkles className="w-3 h-3" /> {t("directory.freshlyVerified")}{month ? ` ${month}` : ""}
    </span>
  );
};

function CompanyCard({ c }) {
  return (
    <Link to={`/directory/${c.id}`} className="group border border-slate-200 bg-white p-5 flex flex-col hover:border-slate-900 transition-colors" data-testid={`company-card-${c.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="w-12 h-12 bg-slate-900 text-white flex items-center justify-center shrink-0"><Building2 className="w-6 h-6" /></div>
        <div className="flex flex-col items-end gap-1">
          {c.oxo_endorsed && <span className="inline-flex items-center gap-1 bg-amber-500 text-slate-900 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5" data-testid="badge-endorsed"><BadgeCheck className="w-3 h-3" /> OXO Endorsed</span>}
          {c.freshly_verified && <FreshlyVerifiedBadge month={c.verified_month} />}
          {c.verified && !c.freshly_verified && <span className="inline-flex items-center gap-1 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5" data-testid="badge-verified"><ShieldCheck className="w-3 h-3" /> Verificat</span>}
        </div>
      </div>
      <h3 className="font-bold text-slate-900 leading-snug group-hover:text-orange-600 transition-colors">{c.company_name}</h3>
      {c.niche && <div className="text-[11px] uppercase tracking-wide font-bold text-orange-600 mt-1">{c.niche}</div>}
      <div className="flex items-center gap-2 text-xs text-slate-500 mt-2"><MapPin className="w-3.5 h-3.5" /> {[c.city, c.county].filter(Boolean).join(", ") || "România"}</div>
      {c.description && <p className="text-sm text-slate-600 mt-3 line-clamp-2">{c.description}</p>}
      <div className="mt-auto flex items-center justify-between pt-4 border-t border-slate-100">
        <TrustStars rating={c.trust_rating} />
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-500"><Package className="w-3.5 h-3.5" /> {c.listing_count} oferte</span>
      </div>
      {c.phone && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(waHref(c.phone, `Bună ziua! V-am găsit pe OXO.ro (${c.company_name}). Aș dori mai multe informații.`), "_blank", "noopener"); }}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors"
          data-testid={`company-whatsapp-${c.id}`}
        >
          <MessageCircle className="w-4 h-4" /> WhatsApp Contact
        </button>
      )}
    </Link>
  );
}

export default function Directory() {
  const [companies, setCompanies] = useState([]);
  const [niches, setNiches] = useState([]);
  const [counties, setCounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ niche: "", county: "", search: "" });
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    api.get("/ro-locations").then((r) => setCounties(r.data.counties)).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setTimeout(() => setFilters((f) => ({ ...f, search: searchInput.trim() })), 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  const fetchCompanies = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filters.niche) params.niche = filters.niche;
    if (filters.county) params.county = filters.county;
    if (filters.search) params.search = filters.search;
    api.get("/directory", { params })
      .then((r) => { setCompanies(r.data.companies); setNiches(r.data.niches || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="directory-page">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-orange-600 mb-2">
          <Building2 className="w-3.5 h-3.5" /> Cégjegyzék · Directory
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Companii verificate din industria lemnului</h1>
        <p className="text-slate-500 mt-2 max-w-2xl">Gatere, uscătorii, comercianți de utilaje, exploatări forestiere și furnizori de servicii — cu rating de încredere OXO.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Caută companie, oraș, descriere..."
            className="w-full pl-9 pr-3 py-2.5 border border-slate-300 text-sm focus:outline-none focus:border-orange-500"
            data-testid="directory-search" />
        </div>
        <Select value={filters.niche || "__all"} onValueChange={(v) => setFilters((f) => ({ ...f, niche: v === "__all" ? "" : v }))}>
          <SelectTrigger className="rounded-none sm:w-64" data-testid="directory-niche"><SelectValue placeholder="Toate nișele" /></SelectTrigger>
          <SelectContent className="max-h-[50vh]">
            <SelectItem value="__all">Toate nișele</SelectItem>
            {niches.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.county || "__all"} onValueChange={(v) => setFilters((f) => ({ ...f, county: v === "__all" ? "" : v }))}>
          <SelectTrigger className="rounded-none sm:w-48" data-testid="directory-county"><SelectValue placeholder="Toate județele" /></SelectTrigger>
          <SelectContent className="max-h-[50vh]">
            <SelectItem value="__all">Toate județele</SelectItem>
            {counties.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="text-sm text-slate-500 mb-4" data-testid="directory-count">{loading ? "Se încarcă..." : `${companies.length} companii`}</div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => <div key={i} className="border border-slate-200 h-56 animate-pulse bg-slate-100" />)
        ) : companies.length === 0 ? (
          <div className="col-span-full border border-slate-200 p-12 text-center" data-testid="no-companies">
            <p className="text-slate-500">Nicio companie pentru filtrele selectate.</p>
          </div>
        ) : (
          companies.map((c) => <CompanyCard key={c.id} c={c} />)
        )}
      </div>
    </div>
  );
}
